import { FormEvent, useEffect, useMemo, useState } from "react";
import { TableSkeleton } from "../../components/Loading";
import { Pager } from "../../components/Pager";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { adminApi, type Company, type CompanyCreateBody, type CompanyKind, type CompanyStatus, type PriceList, type User } from "../../api/admin";
import type { ApiError } from "../../api/client";
import { useToast } from "../../components/Toast";
import { useConfirm } from "../../components/ConfirmDialog";
import { countryName, flagEmoji } from "../../i18n/countries";
import type { FilterOption, ImenovanaOpcija } from "../../api/admin";
import { CountrySelect } from "../../components/CountrySelect";
import { ErpCustomerFields, checkErpCustomer, trimErpCustomer, type ErpCustomer } from "../../components/ErpCustomerFields";
import type { ErpGroupOption } from "../../api/leads";
import { Select } from "../../components/Select";
import { SUPPORTED_LOCALES, type SupportedLocale } from "../../i18n";
import { localeFlag, localeLabel } from "../../i18n/LocaleSwitcher";
import { useAuth } from "../../auth/AuthContext";
import Ic from "../../components/icons.jsx";
import { usePage } from "../../lib/usePage";
import { useUrlParam } from "../../lib/useUrlParam";
import { useUrlZapis } from "../../lib/useUrlZapis";

// Tabs, search and filters all run on the server: the page asks for one page of one tab, and the
// badge counts come from their own endpoint. Nothing here filters `items` — it holds 50 rows out of
// 13,723, so a filter over it would find only what the current page happens to contain.
//
// The place in the list — tab and page — lives in the URL, so leaving a company and coming back
// returns to the same rows.
//
// A customer is a customer because of what it IS (kind), not because someone at
// that firm happens to have a portal login: most of Bankom's ~290 customers will
// never have one, they are worked through their contact person. Portal access is
// shown as a per-row marker instead of splitting the list in two.
/**
 * Koliko firmi ide u jednu stranu.
 *
 * Bilo je 1000. Sa 13.723 firme taj zahtev traje sekundama, a za to vreme se na ekranu vrti
 * spiner koji se prekida na svako ponovno iscrtavanje — otud utisak da treperi. Manja strana
 * i kostur tabele umesto spinera rešavaju i sporost i treperenje.
 */
const PAGE_SIZE = 50;

type CompanyTab = "customers" | "suppliers" | "all";
/** Dozvoljene vrednosti taba u adresi. Adresu piše i čovek, pa se nepoznata vrednost odbija. */
const TABOVI: readonly CompanyTab[] = ["customers", "suppliers", "all"];

/** A company counts as a customer (Kupac) when its kind is CUSTOMER or BOTH.
 * SUPPLIER-only firms are never customers. */
function isCustomerKind(c: Company): boolean {
  return c.kind === "CUSTOMER" || c.kind === "BOTH";
}

/** Cooperants/suppliers (otkup side); BOTH-kind firms appear under both tabs. */
function isSupplierKind(c: Company): boolean {
  return c.kind === "SUPPLIER" || c.kind === "BOTH";
}

export default function AdminCompaniesPage() {
  const { hasPermission } = useAuth();
  const canWrite = hasPermission("section:companies:write");
  const canFull = hasPermission("section:companies:full");
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "en";
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const confirm = useConfirm();
  const [items, setItems] = useState<Company[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Company | null>(null);
  const [creating, setCreating] = useState(false);
  /**
   * Tab živi u adresi, kao i strana.
   *
   * Dok je stajao u `useState`, mesto u spisku je bilo pola sačuvano: povratak iz jedne firme
   * vraćao je stranu i gubio tab, pa je korisnik sa sedme strane dobavljača osvanuo na sedmoj
   * strani KUPACA. Mesto u spisku je par (tab, strana) i oba pripadaju adresi.
   *
   * Promena taba briše stranu u istom upisu: strana drugog taba nije strana ovog.
   */
  const [tab, setTab] = useUrlParam<CompanyTab>("tab", "customers", TABOVI, ["page"]);
  /**
   * Filteri spiska žive u adresi, kao tab i strana — i menjaju se JEDNIM upisom.
   *
   * Dok su stajali u `useState`, mesto u spisku je bilo pola sačuvano i posle ispravke taba:
   * komercijalista suzi dobavljače na svog referenta, otvori firmu i vrati se — tab i strana se
   * vrate, a spisak je ponovo nesužen i pun. Nad 13.767 firmi to znači da se filter postavlja
   * ponovo posle svake firme.
   *
   * Jedan zapis a ne četiri hooka: menjaju se u grupi (dugme „očisti"), a `setSearchParams` gradi
   * adresu iz parametara tekućeg iscrtavanja, pa bi od četiri brisanja preživelo samo poslednje.
   * Svaka izmena poništava stranu — strana sužemog spiska nije strana nesuženog.
   *
   * Referent je filter TABA DOBAVLJAČA; na kupcima se ne nudi i ne primenjuje.
   */
  const [filteri, izmeniFilter] = useUrlZapis(["q", "country", "line", "referent"] as const, ["page"]);
  const { q: search, country, line, referent: otkupAccountId } = filteri;
  const [options, setOptions] = useState<{
    countries: FilterOption[];
    lines: FilterOption[];
    otkupOfficers: ImenovanaOpcija[];
  }>({
    countries: [],
    lines: [],
    otkupOfficers: [],
  });
  const [serverCounts, setServerCounts] = useState<{ customers: number; suppliers: number; all: number } | null>(null);
  const [total, setTotal] = useState(0);
  const [searching, setSearching] = useState(false);
  const [page, setPage] = usePage();

  // The tab maps to a kind in the query. BOTH-kind firms answer to both tabs, which the
  // server handles; the client no longer decides who is a customer.
  const kindForTab = (x: CompanyTab) => (x === "customers" ? "CUSTOMER" : x === "suppliers" ? "SUPPLIER" : undefined);

  async function load(
    nextTab: CompanyTab = tab,
    nextSearch: string = search,
    nextCountry: string = country,
    nextLine: string = line,
    strana: number = page,
    nextReferent: string = otkupAccountId,
  ) {
    setError(null);
    setSearching(true);
    try {
      const result = await adminApi.listCompanies(
        strana,
        PAGE_SIZE,
        kindForTab(nextTab),
        nextSearch,
        nextCountry,
        // Linija je kupčev pojam, referent je dobavljačev: svaki tab šalje samo svoj.
        nextTab === "suppliers" ? undefined : nextLine,
        nextTab === "suppliers" ? nextReferent : undefined,
      );
      setItems(result.content.filter(Boolean));
      const ukupno = result.totalElements ?? result.content.length;
      setTotal(ukupno);
      // Strana iznad poslednje se svodi, i to ODMAH kad se zna ukupan broj.
      //
      // Tab i strana sada stoje u adresi, pa je `?tab=suppliers&page=7` link koji se šalje i čuva.
      // Spisak se u međuvremenu smanji — filter, obrisana firma, drugi tab — i ta strana više ne
      // postoji. Bez svođenja stračnjar piše „7/6" i „301–290 od 290" nad praznom tabelom, uz
      // rečenicu da u spisku još nema firmi. Tri netačnosti nad istim podatkom.
      //
      // Svodi se ovde, a ne u stračnjaru: `Pager` je prikazna komponenta, crta se dvaput na ekranu
      // i ne drži ni stanje strane ni dohvat, pa bi svođenje u njemu ili lagalo prikazom ili
      // pozvalo dohvat dvaput.
      const poslednja = ukupno > 0 ? Math.ceil(ukupno / PAGE_SIZE) - 1 : 0;
      if (strana > poslednja) setPage(poslednja);
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setSearching(false);
    }
  }

  async function loadCounts() {
    try {
      setServerCounts(await adminApi.companyCounts());
    } catch {
      // A failed count must not hide the list; the badges fall back to the loaded page.
      setServerCounts(null);
    }
  }

  useEffect(() => {
    void loadCounts();
    // Padajući spiskovi nude samo ono što u bazi postoji: izmišljena država vraća prazan spisak.
    adminApi
      .companyFilters()
      .then(setOptions)
      .catch(() => setOptions({ countries: [], lines: [], otkupOfficers: [] }));
  }, []);

  // Reset strane pri promeni filtera više ne stoji ovde: svaki upis filtera poništava stranu u
  // istom upisu u adresu (`useUrlZapis(..., ["page"])`). Dok je bio efekt, tražio je čuvar
  // prethodnog ključa — jer efekt radi i pri PRVOM iscrtavanju, pa je dolazak na spisak sa
  // `?page=7` odmah brisao stranu i vraćao čoveka na prvu. Tog čuvara sada nema jer nema ni efekta.

  // The delay is only for a typed word; a page change goes out at once.
  useEffect(() => {
    const id = setTimeout(
      () => void load(tab, search, country, line, page, otkupAccountId),
      search ? 450 : 0,
    );
    return () => clearTimeout(id);
  }, [tab, search, country, line, page, otkupAccountId]);

  async function handleDelete(c: Company) {
    if (!(await confirm({ message: t("admin.companies.confirmDelete", { name: c.name }), danger: true }))) return;
    try {
      await adminApi.deleteCompany(c.id);
      await load();
      toast.success(t("admin.companies.toast.deleted", { name: c.name }));
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  // Counts come from the server, over every visible company rather than the loaded page: with
  // thirteen thousand firms a page-derived badge reads whatever the page happens to hold, which
  // is how "Kupci 0" appeared beside a list full of customers. The loaded page is only a
  // fallback for when the count request fails.
  const counts = useMemo(() => {
    if (serverCounts) return serverCounts;
    const all = items ?? [];
    return {
      customers: all.filter(isCustomerKind).length,
      suppliers: all.filter(isSupplierKind).length,
      all: all.length,
    };
  }, [items, serverCounts]);

  // The server already returned the right kind and the right search hits.
  const byTab = useMemo(() => items ?? [], [items]);

  // Search runs in the database (name, VAT, registration number, city, country, tags), so what
  // comes back is already the answer. Searching the loaded page would only ever find what the
  // page happened to hold.
  const filtered = byTab;

  // Pretraga i filteri su jedno: prazan rezultat se briše istim dugmetom kojim je i sužen.
  const suzeno =
    search.trim() !== "" || country !== "" || (tab === "suppliers" ? otkupAccountId !== "" : line !== "");
  // Sva četiri u JEDNOM upisu. Četiri odvojena poziva bi čitala istu osnovu i poslednji bi
  // pregazio prethodna tri, pa bi od brisanja preživelo samo jedno — spisak bi ostao sužen, a
  // dugmeta kojim se to briše više ne bi bilo.
  const ocisti = () => izmeniFilter({ q: "", country: "", line: "", referent: "" });

  const tabs: { id: CompanyTab; label: string; count: number }[] = [
    { id: "customers", label: t("admin.companies.tab.customers", "Kupci"), count: counts.customers },
    { id: "suppliers", label: t("admin.companies.tab.suppliers", "Suppliers"), count: counts.suppliers },
    { id: "all", label: t("admin.companies.tab.all", "All companies"), count: counts.all },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <header style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h1 style={{ margin: 0, fontSize: 24, color: "var(--brand-primary)" }}>{t("admin.companies.title")}</h1>
        <button type="button" disabled={!canWrite} onClick={() => setCreating(true)} style={primary}>{t("admin.companies.newCompany")}</button>
      </header>

      {error && <div style={errorBox}>{error}</div>}

      {/* Prazan rezultat ne sme da skloni kartice i pretragu: bez njih se pogrešno ukucan
          naziv ne može ispraviti, a poruka "još nema kompanija" tvrdi da je baza prazna.
          Koja je poruka tačna odlučuje se niže, uz samu listu. */}
      {!items ? (
        <TableSkeleton rows={8} cols={7} label={t("common.loading")} />
      ) : (
        <>
          <div role="tablist" aria-label={t("admin.companies.title")} style={tabBar}>
            {tabs.map((tb) => (
              <button
                key={tb.id}
                type="button"
                role="tab"
                aria-selected={tab === tb.id}
                onClick={() => setTab(tb.id)}
                style={tabBtn(tab === tb.id)}
              >
                {tb.label}
                <span style={tabCount(tab === tb.id)}>{tb.count}</span>
              </button>
            ))}
          </div>

          <section style={filterCard}>
            <div style={filterRow}>
              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={filterLabel}>{t("admin.companies.searchLabel", "Search")}</span>
                <div style={{ position: "relative" }}>
                  <span style={searchIcon}><Ic.search /></span>
                  <input
                    value={search}
                    onChange={(e) => izmeniFilter({ q: e.target.value })}
                    placeholder={t("admin.companies.searchPlaceholder", "Name, VAT, country…")}
                    className="input"
                    style={{ width: 280, maxWidth: "100%", paddingLeft: 30 }}
                  />
                </div>
              </label>

              <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={filterLabel}>{t("admin.companies.col.country")}</span>
                <select
                  value={country}
                  onChange={(e) => izmeniFilter({ country: e.target.value })}
                  className="input"
                  style={{ width: 200, maxWidth: "100%" }}
                >
                  <option value="">{t("admin.companies.filter.allCountries", "Sve države")}</option>
                  {options.countries.map((o) => (
                    <option key={o.value} value={o.value}>
                      {`${flagEmoji(o.value)} ${countryName(o.value, locale)} (${o.count})`}
                    </option>
                  ))}
                </select>
              </label>

              {/* Drugi filter prati tab, jer kupac i dobavljač nisu isti posao: linija je ono što
                  kupac KUPUJE, a referent je čovek koji radi sa kooperantom. Linija na
                  tabu dobavljača nije suvišna nego netačna — nudi izbor koji o dobavljaču ništa ne
                  kaže. */}
              {tab === "suppliers" ? (
                <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={filterLabel}>{t("admin.companies.col.otkupRep", "Referent otkupa")}</span>
                  <select
                    value={otkupAccountId}
                    onChange={(e) => izmeniFilter({ referent: e.target.value })}
                    className="input"
                    style={{ width: 230, maxWidth: "100%" }}
                  >
                    <option value="">{t("admin.companies.filter.allOtkupReps", "Svi referenti")}</option>
                    {options.otkupOfficers.map((o) => (
                      <option key={o.value} value={o.value}>{`${o.label} (${o.count})`}</option>
                    ))}
                  </select>
                </label>
              ) : (
                <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={filterLabel}>{t("admin.companies.col.tags", "Product lines")}</span>
                  <select
                    value={line}
                    onChange={(e) => izmeniFilter({ line: e.target.value })}
                    className="input"
                    style={{ width: 230, maxWidth: "100%" }}
                  >
                    <option value="">{t("admin.companies.filter.allLines", "Sve linije")}</option>
                    {options.lines.map((o) => (
                      <option key={o.value} value={o.value}>{`${o.value} (${o.count})`}</option>
                    ))}
                  </select>
                </label>
              )}
            </div>

            <div style={filterSummary}>
              <span style={{ color: "var(--ink-3)", fontSize: 12.5 }}>
                {t("admin.companies.shown", { shown: filtered.length, total, defaultValue: "Showing {{shown}} of {{total}}" })}
              </span>
              {suzeno && (
                <button type="button" className="btn ghost sm" onClick={ocisti}>
                  <Ic.x /> {t("admin.companies.clearSearch", "Clear")}
                </button>
              )}
            </div>
          </section>

          <Pager page={page} size={PAGE_SIZE} total={total} busy={searching} onPage={setPage} />

          {filtered.length === 0 ? (
            <div style={{ color: "var(--ink-3)", display: "flex", alignItems: "center", gap: 10 }}>
              <span>
                {searching
                  ? t("common.loading")
                  : suzeno
                    ? t("admin.companies.filterEmpty", "No companies match your search.")
                    : t("admin.companies.tabEmpty", "No companies in this list yet.")}
              </span>
              {suzeno && !searching && (
                <button type="button" className="btn ghost sm" onClick={ocisti}>
                  <Ic.x /> {t("admin.companies.clearSearch", "Clear")}
                </button>
              )}
            </div>
          ) : (
            <>
            <table className="list-card">
              <thead>
                <tr>
                  <th style={th}>{t("admin.companies.col.name")}</th>
                  <th style={th}>{t("admin.companies.col.contact", "Contact")}</th>
                  <th style={th}>{t("admin.companies.col.country")}</th>
                  <th style={th}>
                    {tab === "suppliers"
                      ? t("admin.companies.col.otkupRep", "Referent otkupa")
                      : t("admin.companies.col.tags", "Product lines")}
                  </th>
                  <th style={th}>{t("admin.companies.col.locale")}</th>
                  <th style={th}>{t("admin.companies.col.status")}</th>
                  <th style={{ ...th, textAlign: "right" }}>{t("admin.companies.col.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr
                    key={c.id}
                    /* Spisak nosi svoje mesto u adresi (tab i strana). Detalj ga dobija kao
                       stanje navigacije, pa mrvica ume da vrati tačno tamo odakle se došlo — i kad
                       se do detalja dođe direktnim linkom, gde tog stanja nema, pa se pada na goli
                       spisak. */
                    onClick={() =>
                      navigate(`/admin/companies/${c.id}`, {
                        state: { nazad: `${location.pathname}${location.search}` },
                      })
                    }
                    style={{ cursor: "pointer" }}
                    className="row-link"
                  >
                    <td style={td} data-label={t("admin.companies.col.name")}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span>{c.name}</span>
                        {c.registered && (
                          <span style={portalChip} title={t("admin.companies.portalAccess.hint", "Has at least one portal login")}>
                            {t("admin.companies.portalAccess.yes", "Portal")}
                          </span>
                        )}
                      </span>
                    </td>
                    <td style={td} data-label={t("admin.companies.col.contact", "Contact")}>
                      {c.contactName ? (
                        <>
                          <div style={{ color: "var(--ink)" }}>{c.contactName}</div>
                          {c.contactEmail && (
                            <div style={{ color: "var(--ink-3)", fontSize: 12 }}>{c.contactEmail}</div>
                          )}
                        </>
                      ) : (
                        <span style={{ color: "var(--ink-4)" }}>—</span>
                      )}
                    </td>
                    <td style={td} data-label={t("admin.companies.col.country")}>
                      {c.country ? (
                        <span style={{ fontFamily: emojiFontStack }}>
                          {flagEmoji(c.country)} {countryName(c.country, locale)}
                        </span>
                      ) : (
                        "—"
                      )}
                      {/* Sedište je upisano na skoro svakoj firmi; bez grada spisak zna samo državu. */}
                      {c.city && <div style={{ color: "var(--ink-3)", fontSize: 12 }}>{c.city}</div>}
                    </td>
                    {tab === "suppliers" ? (
                      <td style={td} data-label={t("admin.companies.col.otkupRep", "Referent otkupa")}>
                        {c.otkupAccountName ? (
                          <span style={tagChip}>{c.otkupAccountName}</span>
                        ) : (
                          /* Nedodeljen kooperant se imenuje, ne pokazuje crtom: referent je ono po
                             čemu se otkupni posao deli, pa je prazno ovde podatak a ne izostanak. */
                          <span style={{ color: "var(--ink-3)", fontStyle: "italic" }}>
                            {t("admin.companies.noOtkupRep", "nije dodeljen")}
                          </span>
                        )}
                      </td>
                    ) : (
                      <td style={td} data-label={t("admin.companies.col.tags", "Product lines")}>
                        {c.tags ? (
                          <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 4 }}>
                            {c.tags.split(",").map((s) => s.trim()).filter(Boolean).map((tag) => (
                              <span key={tag} style={tagChip}>{tag}</span>
                            ))}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                    )}
                    <td style={td} data-label={t("admin.companies.col.locale")}>
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        padding: "2px 10px", borderRadius: 999,
                        background: "var(--surface-2)", border: "1px solid var(--border)",
                        fontSize: 12, fontWeight: 500, color: "var(--ink-2)",
                      }}>
                        <span style={{ fontSize: 14, lineHeight: 1 }}>{localeFlag(c.defaultLocale as SupportedLocale)}</span>
                        {localeLabel(c.defaultLocale as SupportedLocale)}
                      </span>
                    </td>
                    <td style={td} data-label={t("admin.companies.col.status")}>
                      <StatusBadge status={c.status} />
                    </td>
                    <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }} className="card-actions">
                      <Link
                        to={`/admin/companies/${c.id}`}
                        style={linkBtn}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {t("admin.companies.action.details")}
                      </Link>
                      <button
                        type="button"
                        disabled={!canWrite} onClick={(e) => { e.stopPropagation(); setEditing(c); }}
                        style={linkBtn}
                      >
                        {t("admin.companies.action.edit")}
                      </button>
                      <button
                        type="button"
                        disabled={!canFull} onClick={(e) => { e.stopPropagation(); void handleDelete(c); }}
                        style={dangerLink}
                      >
                        {t("admin.companies.action.delete")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pager page={page} size={PAGE_SIZE} total={total} busy={searching} onPage={setPage} />
            </>
          )}
        </>
      )}

      {canWrite && creating && (
        <CompanyFormModal
          title={t("admin.companies.modal.new")}
          onClose={() => setCreating(false)}
          onSubmit={async (body) => {
            await adminApi.createCompany(body);
            setCreating(false);
            await load();
          }}
        />
      )}

      {canWrite && editing && (
        <CompanyFormModal
          title={t("admin.companies.modal.edit")}
          initial={editing}
          onClose={() => setEditing(null)}
          onSubmit={async (body) => {
            await adminApi.updateCompany(editing.id, body);
            setEditing(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

export function CompanyFormModal({
  title,
  initial,
  onSubmit,
  onClose,
}: {
  title: string;
  initial?: Company;
  onSubmit: (body: CompanyCreateBody) => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { hasPermission, state } = useAuth();
  const isCreate = !initial;
  const currentUserId = state?.status === "authenticated" ? state.user.id : "";
  // The otkup-officer picker stays an administrator's (market settings). The sales-rep picker below is
  // broader: whoever opens a customer picks who is responsible for it, since the customer goes to the ERP
  // under that rep.
  const canPickSales = isCreate && hasPermission("section:market_settings:full");
  // The kind of a company from the ERP comes from its ERP turnover, and the server refuses another
  // one, so the form shows that kind without offering a choice.
  const kindFromErp = !isCreate && !!initial?.erpId;

  const [name, setName] = useState(initial?.name ?? "");
  // A new company is a customer unless picked otherwise, as on the server. An existing one keeps its
  // kind, which may be none: the form never fills one in on its own.
  const [kind, setKind] = useState<CompanyKind | "">(isCreate ? "CUSTOMER" : (initial?.kind ?? ""));
  const kindLabel: Record<CompanyKind, string> = {
    CUSTOMER: t("admin.companies.kind.CUSTOMER", "Customer"),
    SUPPLIER: t("admin.companies.kind.SUPPLIER", "Supplier"),
    BOTH: t("admin.companies.kind.BOTH", "Customer and supplier"),
  };
  const [vat, setVat] = useState(initial?.vatNumber ?? "");
  const [erpId, setErpId] = useState(initial?.erpId ?? "");
  const [country, setCountry] = useState(initial?.country ?? "");
  const [addressLine1, setAddressLine1] = useState(initial?.addressLine1 ?? "");
  const [addressLine2, setAddressLine2] = useState(initial?.addressLine2 ?? "");
  const [city, setCity] = useState(initial?.city ?? "");
  const [postalCode, setPostalCode] = useState(initial?.postalCode ?? "");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [erpGroup, setErpGroup] = useState("");
  const [erpGroups, setErpGroups] = useState<ErpGroupOption[]>([]);
  // A new customer goes to the ERP, entered the same as a converted lead. One given an ERP ID is already there,
  // and a supplier does not go from here.
  const toErp = isCreate && kind !== "SUPPLIER" && erpId.trim() === "";
  const erp: ErpCustomer = { vatNumber: vat, registrationNumber, country, addressLine1, postalCode, city, erpGroup };
  const readyForErp = checkErpCustomer(erp).ready;
  function setErp(next: ErpCustomer) {
    setVat(next.vatNumber);
    setRegistrationNumber(next.registrationNumber);
    setCountry(next.country);
    setAddressLine1(next.addressLine1);
    setPostalCode(next.postalCode);
    setCity(next.city);
    setErpGroup(next.erpGroup);
  }
  const [locale, setLocale] = useState<SupportedLocale>(
    (initial?.defaultLocale as SupportedLocale) ?? "en",
  );
  const [status, setStatus] = useState<CompanyStatus>(initial?.status ?? "ACTIVE");
  const [displayDiscount, setDisplayDiscount] = useState(initial?.displayDiscount ?? true);
  const [forecastsEnabled, setForecastsEnabled] = useState(initial?.forecastsEnabled ?? true);
  const [salesUsers, setSalesUsers] = useState<User[]>([]);
  const [assignedSalesUserId, setAssignedSalesUserId] = useState("");
  const [serviceUsers, setServiceUsers] = useState<User[]>([]);
  const [otkupAccountId, setOtkupAccountId] = useState("");
  const [priceLists, setPriceLists] = useState<PriceList[]>([]);
  const [priceListId, setPriceListId] = useState(initial?.priceListId ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isCreate) return;
    void adminApi
      .listUsers(0, 200)
      .then((p) => {
        const all = p.content.filter(Boolean).filter((u) => u.status === "ACTIVE");
        setSalesUsers(all.filter((u) => u.roles.includes("SALES") || u.roles.includes("SALES_DIRECTOR")));
        setServiceUsers(canPickSales ? all.filter((u) => u.internal && u.roles.includes("OTKUP")) : []);
      })
      .catch(() => { setSalesUsers([]); setServiceUsers([]); });
  }, [isCreate, canPickSales]);

  // Pre-select the creator as the responsible sales rep when they are one (a sales director or sales user
  // opening a customer by hand). The field stays visible and they can pick someone else.
  useEffect(() => {
    if (!isCreate || assignedSalesUserId) return;
    if (currentUserId && salesUsers.some((u) => u.id === currentUserId)) {
      setAssignedSalesUserId(currentUserId);
    }
  }, [isCreate, currentUserId, salesUsers, assignedSalesUserId]);

  useEffect(() => {
    void adminApi.listPriceLists().then(setPriceLists).catch(() => setPriceLists([]));
  }, []);

  useEffect(() => {
    if (!isCreate) return;
    void adminApi.erpGroups().then(setErpGroups).catch(() => setErpGroups([]));
  }, [isCreate]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    // Select is a <button>, so it carries no native `required`. A customer
    // cannot be created without a price list — the API refuses it too.
    if (isCreate && !priceListId) {
      setError(t("pricelist.required", "Pick a price list."));
      return;
    }
    if (toErp && !readyForErp) {
      setError(t("erpCustomer.intro", "The customer goes to the ERP at once. Required: tax number, registration number, country, customer kind and address."));
      return;
    }
    // A customer goes to the ERP under its responsible sales rep (komercijalista). Ask for one when we
    // have the sales reps to pick from, so the ERP does not reject the customer for a missing rep.
    if (toErp && salesUsers.length > 0 && !assignedSalesUserId) {
      setError(t("admin.companies.assignedSales.required", "Pick the sales rep responsible for this customer — the customer goes to the ERP under them."));
      return;
    }
    const forErp = toErp ? trimErpCustomer(erp) : null;
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        // Sent only when picked: a company left without a kind stays without one.
        kind: kind !== "" && (isCreate || (!kindFromErp && kind !== initial?.kind)) ? kind : undefined,
        vatNumber: vat.trim() || undefined,
        erpId: erpId.trim() || undefined,
        country: country.trim() || undefined,
        addressLine1: addressLine1.trim() || undefined,
        addressLine2: addressLine2.trim() || undefined,
        city: city.trim() || undefined,
        postalCode: postalCode.trim() || undefined,
        ...(forErp ?? {}),
        defaultLocale: locale,
        status,
        displayDiscount,
        forecastsEnabled,
        assignedSalesUserId: assignedSalesUserId || undefined,
        otkupAccountId: canPickSales && otkupAccountId ? otkupAccountId : undefined,
        priceListId: priceListId || undefined,
      });
    } catch (err) {
      setError((err as ApiError).message);
    } finally {
      setSubmitting(false);
    }
  }

  // Kept next to the rest of the address: below the ERP fields for a customer going to the ERP.
  const addressLine2Field = (
    <Field label={t("admin.companies.field.addr2", "Address line 2")}>
      <input
        value={addressLine2}
        onChange={(e) => setAddressLine2(e.target.value)}
        placeholder={t("admin.companies.field.addr2Placeholder", "Apartment, suite, etc. (optional)")}
        style={input}
      />
    </Field>
  );
  const saveBlocked = submitting || (toErp && !readyForErp);

  return (
    <Modal onClose={onClose}>
      <h2 style={{ margin: 0, fontSize: 18, color: "var(--brand-primary)" }}>{title}</h2>
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Field label={t("admin.companies.field.name", "Name")} required>
          <input value={name} onChange={(e) => setName(e.target.value)} required style={input} />
        </Field>
        <Field label={t("admin.companies.field.kind", "Company type")} required={isCreate}>
          {kindFromErp ? (
            <>
              <span data-testid="company-kind-from-erp">
                {kind === "" ? t("admin.companies.kind.none", "Not set") : kindLabel[kind]}
              </span>
              <span style={{ color: "var(--ink-3)", fontSize: 12, marginTop: 4 }}>
                {t("admin.companies.hint.kindFromErp", "Derived from the company's turnover in the ERP, so it is not edited here.")}
              </span>
            </>
          ) : (
            <Select<CompanyKind | "">
              value={kind}
              onChange={setKind}
              ariaLabel={t("admin.companies.field.kind", "Company type")}
              placeholder={t("admin.companies.kind.none", "Not set")}
              options={(["CUSTOMER", "SUPPLIER", "BOTH"] as const).map((k) => ({ value: k, label: kindLabel[k] }))}
            />
          )}
        </Field>
        {!toErp && (
          <Field label={t("admin.companies.field.vatNumber", "VAT number")}>
            <input value={vat} onChange={(e) => setVat(e.target.value)} style={input} />
          </Field>
        )}
        <Field label={t("admin.companies.field.erpId", "ERP ID")}>
          <input value={erpId} onChange={(e) => setErpId(e.target.value)} style={input} maxLength={64}
                 placeholder={t("admin.companies.field.optional", "optional")} />
          {isCreate && kind !== "SUPPLIER" && (
            <span style={{ color: "var(--ink-3)", fontSize: 12, marginTop: 4 }}>
              {t("erpCustomer.inErpHint", "Only for a customer already in the ERP. Without it, the customer goes to the ERP when saved.")}
            </span>
          )}
        </Field>
        {toErp && (
          <section aria-label={t("erpCustomer.section", "For the ERP")} style={erpBox}>
            <div style={{ fontWeight: 600, color: "var(--ink)" }}>{t("erpCustomer.section", "For the ERP")}</div>
            <p style={{ margin: 0, color: "var(--ink-2)", fontSize: 13 }}>
              {t("erpCustomer.intro", "The customer goes to the ERP at once. Required: tax number, registration number, country, customer kind and address.")}
            </p>
            <ErpCustomerFields value={erp} onChange={setErp} groups={erpGroups} />
          </section>
        )}
        {toErp && addressLine2Field}

        <Field label={t("admin.companies.field.displayDiscount", "Display discount")}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "var(--ink-2)" }}>
            <input type="checkbox" checked={displayDiscount} onChange={(e) => setDisplayDiscount(e.target.checked)} />
            {t("admin.companies.hint.displayDiscount", "Show the discount % to this customer in their cart")}
          </label>
        </Field>

        <Field label={t("admin.companies.field.forecasts", "Does Forecasts?")}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, color: "var(--ink-2)" }}>
            <input type="checkbox" checked={forecastsEnabled} onChange={(e) => setForecastsEnabled(e.target.checked)} />
            {t("admin.companies.hint.forecasts", "This customer submits demand forecasts (shows the Forecasts section to them and their sales)")}
          </label>
        </Field>

        <Field label={t("pricelist.label", "Price list")} required={isCreate}>
          <Select<string>
            value={priceListId}
            onChange={setPriceListId}
            ariaLabel={t("pricelist.label", "Price list")}
            options={[
              { value: "", label: t("pricelist.choose", "— Select price list —") },
              ...priceLists.map((pl) => ({
                value: pl.id,
                label: `${pl.name}${pl.isDefault ? ` (${t("pricelist.reference", "reference")})` : ""}`,
              })),
            ]}
          />
        </Field>

        {!toErp && (
          <Field label={t("admin.companies.field.addr1", "Address line 1")}>
            <input
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              placeholder={t("admin.companies.field.addr1Placeholder", "Street and number")}
              style={input}
            />
          </Field>
        )}
        {!toErp && addressLine2Field}
        {!toErp && (
          <>
            <div className="form-grid-2" style={{ display: "grid", gridTemplateColumns: "1fr 140px", gap: 12 }}>
              <Field label={t("admin.companies.field.city", "City")}>
                <input value={city} onChange={(e) => setCity(e.target.value)} style={input} />
              </Field>
              <Field label={t("admin.companies.field.postal", "Postal code")}>
                <input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} style={input} />
              </Field>
            </div>

            <Field label={t("admin.companies.field.country", "Country")}>
              <CountrySelect value={country} onChange={setCountry} />
            </Field>
          </>
        )}

        <Field label={t("admin.companies.field.language", "Default language")}>
          <Select<SupportedLocale>
            value={locale}
            onChange={setLocale}
            options={SUPPORTED_LOCALES.map((l) => ({
              value: l,
              label: localeLabel(l),
              left: <span style={{ fontFamily: emojiFontStack }}>{localeFlag(l)}</span>,
            }))}
          />
        </Field>
        <Field label={t("admin.companies.field.status", "Status")}>
          <Select<CompanyStatus>
            value={status}
            disabled={!hasPermission("section:companies:full")}
            onChange={setStatus}
            options={[
              { value: "ACTIVE", label: t("admin.companies.status.ACTIVE", "Active") },
              { value: "SUSPENDED", label: t("admin.companies.status.SUSPENDED", "Suspended") },
            ]}
          />
        </Field>
        {isCreate && salesUsers.length > 0 && (
          <Field label={t("admin.companies.field.assignedSales", "Assigned sales rep")}>
            <Select<string>
              value={assignedSalesUserId}
              onChange={setAssignedSalesUserId}
              options={[
                { value: "", label: t("admin.companies.field.none", "— None (assign later) —") },
                ...salesUsers.map((u) => ({ value: u.id, label: `${u.firstName} ${u.lastName} (${u.email})` })),
              ]}
            />
            <span style={{ color: "var(--ink-3)", fontSize: 12, marginTop: 4 }}>
              {t("admin.companies.hint.assignedSales", "Picked user is set as the company's primary sales rep. Can be changed in Details later.")}
            </span>
          </Field>
        )}
        {canPickSales && (
          <Field label={t("admin.companies.field.otkupRef", "Otkup officer")}>
            <Select<string>
              value={otkupAccountId}
              onChange={setOtkupAccountId}
              options={[
                { value: "", label: t("admin.companies.field.none", "— None (assign later) —") },
                ...serviceUsers.map((u) => ({ value: u.id, label: `${u.firstName} ${u.lastName} (${u.email})` })),
              ]}
            />
            <span style={{ color: "var(--ink-3)", fontSize: 12, marginTop: 4 }}>
              {t("admin.companies.hint.otkupRef", "Internal Otkup staff assigned to this company. Can be changed in Details later.")}
            </span>
          </Field>
        )}
        {error && <div style={errorBox}>{error}</div>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose} style={secondary}>{t("action.cancel", "Cancel")}</button>
          <button type="submit" disabled={saveBlocked} style={saveBlocked ? { ...primary, ...blocked } : primary}>
            {submitting ? t("admin.companies.action.saving", "Saving…") : t("admin.companies.action.save", "Save")}
          </button>
        </div>
      </form>
    </Modal>
  );
}

const emojiFontStack =
  'apple color emoji, segoe ui emoji, noto color emoji, "Inter Tight", system-ui, sans-serif';

function StatusBadge({ status }: { status: CompanyStatus }) {
  const { t } = useTranslation();
  const palette: Record<CompanyStatus, { bg: string; fg: string }> = {
    ACTIVE: { bg: "var(--ok-bg)", fg: "var(--ok)" },
    SUSPENDED: { bg: "var(--warn-bg)", fg: "var(--warn)" },
    DELETED: { bg: "var(--err-bg)", fg: "var(--err)" },
  };
  const c = palette[status];
  return (
    <span style={{ background: c.bg, color: c.fg, padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600 }}>
      {t(`admin.companies.status.${status}`, status)}
    </span>
  );
}

function Modal({ onClose, children }: { onClose: () => void; children: React.ReactNode }) {
  return (
    <div onClick={onClose} className="modal-overlay" style={overlay}>
      <div onClick={(e) => e.stopPropagation()} className="modal-panel" style={dialog}>{children}</div>
    </div>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 13 }}>
      <span style={{ color: "var(--ink-3)" }}>
        {label} {required && <span style={{ color: "var(--err)" }}>*</span>}
      </span>
      {children}
    </label>
  );
}

const th: React.CSSProperties = {
  textAlign: "left",
  padding: "10px 12px",
  background: "var(--surface-2)",
  color: "var(--ink-3)",
  fontWeight: 600,
  borderBottom: "1px solid var(--border)",
};
const td: React.CSSProperties = { padding: "10px 12px", borderBottom: "1px solid var(--border)" };
const portalChip: React.CSSProperties = {
  background: "var(--ok-bg)", color: "var(--ok)", border: "1px solid var(--ok)",
  borderRadius: 999, padding: "1px 8px", fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap",
};
const tagChip: React.CSSProperties = {
  background: "var(--surface-muted)", color: "var(--ink-2)",
  border: "1px solid var(--border)", borderRadius: 4,
  padding: "1px 6px", fontSize: 11, whiteSpace: "nowrap",
};
const input: React.CSSProperties = { padding: "8px 10px", borderRadius: 6, border: "1px solid var(--border-strong)", fontSize: 14 };
const primary: React.CSSProperties = { padding: "8px 14px", background: "var(--brand-primary)", color: "var(--on-primary)", border: "none", borderRadius: 6, fontSize: 14, cursor: "pointer" };
const secondary: React.CSSProperties = { padding: "8px 14px", background: "var(--surface)", border: "1px solid var(--border-strong)", borderRadius: 6, fontSize: 14, cursor: "pointer" };
const linkBtn: React.CSSProperties = { background: "transparent", border: "none", color: "var(--brand-primary)", cursor: "pointer", marginRight: 12, fontSize: 13 };
const dangerLink: React.CSSProperties = { background: "transparent", border: "none", color: "var(--err)", cursor: "pointer", fontSize: 13 };
const errorBox: React.CSSProperties = { padding: 10, background: "var(--err-bg)", color: "var(--err)", borderRadius: 6, fontSize: 13 };
const blocked: React.CSSProperties = { opacity: 0.5, cursor: "not-allowed" };
const erpBox: React.CSSProperties = {
  display: "flex", flexDirection: "column", gap: 10, padding: 14,
  border: "1px solid var(--border)", borderRadius: 8, background: "var(--surface-2)",
};

const tabBar: React.CSSProperties = {
  display: "inline-flex", padding: 4, gap: 4, background: "var(--surface-2)",
  border: "1px solid var(--border)", borderRadius: 999, width: "fit-content", maxWidth: "100%",
  flexWrap: "wrap",
};
function tabBtn(active: boolean): React.CSSProperties {
  return {
    display: "inline-flex", alignItems: "center", gap: 8,
    padding: "6px 16px", borderRadius: 999, fontSize: 13, fontWeight: 600,
    border: "none", cursor: "pointer", transition: "background .12s",
    color: active ? "var(--on-primary)" : "var(--ink-2)",
    background: active ? "var(--brand-primary)" : "transparent",
  };
}
function tabCount(active: boolean): React.CSSProperties {
  return {
    fontSize: 11, fontWeight: 700, padding: "0 7px", borderRadius: 999, lineHeight: "17px",
    background: active ? "color-mix(in srgb, var(--on-primary) 25%, transparent)" : "var(--surface)",
    color: active ? "var(--on-primary)" : "var(--ink-3)",
    border: active ? "none" : "1px solid var(--border)",
  };
}
const filterCard: React.CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-md, 12px)",
  boxShadow: "var(--shadow-1)", padding: 16,
  display: "flex", flexDirection: "column", gap: 12,
};
/** Pretraga i filteri stoje u jednom redu; na uskom ekranu se red prelama. */
const filterRow: React.CSSProperties = {
  display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 12,
};
const filterLabel: React.CSSProperties = {
  display: "block", fontSize: 11, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: 0.4,
};
const searchIcon: React.CSSProperties = {
  position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)",
  width: 15, height: 15, display: "inline-flex", color: "var(--ink-3)", pointerEvents: "none",
};
const filterSummary: React.CSSProperties = {
  display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
  paddingTop: 12, borderTop: "1px solid var(--border)",
};

const overlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "var(--overlay)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 50,
};
const dialog: React.CSSProperties = {
  width: "100%",
  maxWidth: 560,
  maxHeight: "85vh",
  overflowY: "auto",
  background: "var(--surface)",
  padding: 24,
  borderRadius: 12,
  boxShadow: "var(--shadow-2)",
  display: "flex",
  flexDirection: "column",
  gap: 16,
};
